/**
 * useOnboarding Hook
 * Manages onboarding status and provides context across the application
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  onboardingService, 
  OnboardingStatus, 
  OnboardingScenario, 
  OnboardingStage 
} from '@/services/api/onboardingService';
import { sessionManager, SessionExpiredError } from '@/services/sessionManager';
import { waitForAuthReady, personaRefusalFrom } from '@/services/apiClient';
import { OnboardingScenario as UIOnboardingScenario } from '@/types/onboarding';

export interface OnboardingState {
  isLoading: boolean;
  needsOnboarding: boolean;
  status: OnboardingStatus | null;
  scenario: string | null;
  stage: string | null;
  error: Error | null;
}

export function useOnboarding() {
  const [state, setState] = useState<OnboardingState>({
    isLoading: true,
    needsOnboarding: false,
    status: null,
    scenario: null,
    stage: null,
    error: null
  });

  const checkOnboardingStatus = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Boot gate: don't start the 5s watchdog until auth is actually ready —
      // otherwise a boot-time token refresh eats the whole window and the
      // timeout's fail-safe bounces the user toward onboarding.
      await waitForAuthReady();

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Onboarding check timeout')), 5000)
      );
      
      const statusPromise = onboardingService.getStatus();
      const status = await Promise.race([statusPromise, timeoutPromise]) as any;
      
      // Determine if onboarding is needed based on status
      const needsOnboarding = determineNeedsOnboarding(status);
      
      if (import.meta.env.DEV) console.log('✅ Onboarding Status Check Complete:', {
        userId: status?.user_id,
        scenario: status?.onboarding_scenario,
        currentStage: status?.current_stage,
        profileCompleted: status?.profile_completed,
        needsOnboarding,
        willTriggerRedirect: needsOnboarding
      });
      
      setState({
        isLoading: false,
        needsOnboarding,
        status,
        scenario: status.onboarding_scenario || null,
        stage: status.current_stage || null,
        error: null
      });
      
      return status;
    } catch (error) {
      console.error('❌ Failed to check onboarding status:', error);
      
      // If session is expired, do NOT assume onboarding needed — let SessionExpiredModal handle UX
      if (error instanceof SessionExpiredError || sessionManager.isExpired()) {
        console.warn('⚠️ Onboarding status check failed due to expired session - deferring to SessionExpiredModal');
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error as Error
        }));
        return null;
      }
      
      // A refused test persona: AuthProvider drops it and fires personaChanged, which
      // re-checks as the real account. Assuming onboarding here would bounce them first.
      const response = (error as { response?: { status?: number; data?: { detail?: unknown } } }).response;
      if (personaRefusalFrom(response?.status, response?.data?.detail)) {
        return null;
      }

      // For genuine API failures (server down, timeout, etc.):
      // If we already have a valid completed status from a previous successful check, preserve it
      // instead of overwriting — this prevents timeouts on re-checks from bouncing the user to onboarding
      setState(prev => {
        if (prev.status?.current_stage === 'completed' && !prev.needsOnboarding) {
          console.warn('⚠️ Onboarding re-check failed but previous status is completed - preserving state');
          return { ...prev, isLoading: false, error: error as Error };
        }
        // No prior valid state — assume onboarding IS needed (fail-safe for first-time checks)
        console.warn('⚠️ Onboarding status check failed. Assuming onboarding needed for safety.');
        return {
          isLoading: false,
          needsOnboarding: true,
          status: null,
          scenario: null,
          stage: null,
          error: error as Error
        };
      });
      return null;
    }
  }, []);

  const initializeOnboarding = useCallback(async (scenario: UIOnboardingScenario) => {
    const apiScenario = convertUIScenarioToAPI(scenario);
    try {
      const status = await onboardingService.initialize(apiScenario);
      setState(prev => ({
        ...prev,
        needsOnboarding: true,
        status,
        scenario: status.onboarding_scenario || null,
        stage: status.current_stage || null
      }));
      return status;
    } catch (error) {
      console.error('Failed to initialize onboarding:', error);
      setState(prev => ({ ...prev, error: error as Error }));
      return null;
    }
  }, []);

  const updateStage = useCallback(async (newStage: string) => {
    const apiStage = newStage.replace('-', '_') as OnboardingStage;
    try {
      const status = await onboardingService.updateStage(apiStage);
      setState(prev => ({
        ...prev,
        status,
        stage: status.current_stage || null
      }));
      return status;
    } catch (error) {
      console.error('Failed to update stage:', error);
      setState(prev => ({ ...prev, error: error as Error }));
      return null;
    }
  }, []);

  const completeOnboarding = useCallback(async () => {
    try {
      const status = await onboardingService.complete();
      setState(prev => ({
        ...prev,
        needsOnboarding: false,
        status,
        stage: 'completed'
      }));
      return status;
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      setState(prev => ({ ...prev, error: error as Error }));
      return null;
    }
  }, []);

  // Check on mount and when persona changes (skip on public routes)
  useEffect(() => {
    // Public routes don't require authentication — skip onboarding check
    const PUBLIC_ROUTES = ['/feedback/respond'];
    const isPublicRoute = PUBLIC_ROUTES.some(r => window.location.pathname.startsWith(r));
    if (isPublicRoute) {
      setState(prev => ({ ...prev, isLoading: false, needsOnboarding: false }));
      return;
    }

    checkOnboardingStatus();

    // Re-check when persona changes in dev mode
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dev_selected_persona') {
        if (import.meta.env.DEV) console.log('🔄 Persona changed - rechecking onboarding status');
        checkOnboardingStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event when persona changes in same tab
    const handlePersonaChange = () => {
      if (import.meta.env.DEV) console.log('🔄 Persona switched - rechecking onboarding status');
      setTimeout(() => checkOnboardingStatus(), 100); // Small delay to let localStorage update
    };

    window.addEventListener('personaChanged', handlePersonaChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('personaChanged', handlePersonaChange);
    };
  }, [checkOnboardingStatus]);

  return {
    ...state,
    checkOnboardingStatus,
    initializeOnboarding,
    updateStage,
    completeOnboarding
  };
}

/**
 * Determine if user needs onboarding based on their status
 */
function determineNeedsOnboarding(status: OnboardingStatus): boolean {
  // No status means new user - needs onboarding
  if (!status) return true;

  // If onboarding is completed, no need to redirect unless annual update is overdue
  if (status.current_stage === 'completed') {
    // Only redirect if annual update is overdue
    return status.annual_update_overdue || false;
  }

  // Onboarding is in progress - user needs to complete it
  return true;
}

/**
 * Determine onboarding scenario based on user status and context
 */
/**
 * Convert UI scenario format (kebab-case) to API format (snake_case)
 */
function convertUIScenarioToAPI(scenario: UIOnboardingScenario): OnboardingScenario {
  const mapping: Record<UIOnboardingScenario, OnboardingScenario> = {
    'first-time-user': OnboardingScenario.FIRST_TIME_USER,
    'role-change': OnboardingScenario.ROLE_CHANGE,
    'campus-transfer': OnboardingScenario.CAMPUS_TRANSFER,
    'annual-profile-update': OnboardingScenario.ANNUAL_UPDATE
  };
  return mapping[scenario];
}

/**
 * Convert API scenario format (snake_case) to UI format (kebab-case)
 */
export function convertAPIScenarioToUI(scenario: string): UIOnboardingScenario {
  const mapping: Record<string, UIOnboardingScenario> = {
    'first_time_user': 'first-time-user',
    'role_change': 'role-change',
    'campus_transfer': 'campus-transfer',
    'annual_update': 'annual-profile-update'
  };
  return mapping[scenario] || 'first-time-user';
}

/**
 * Determine onboarding scenario based on user status and context
 */
export function determineOnboardingScenario(
  status: OnboardingStatus | null,
  isNewUser: boolean,
  hasRoleChange?: boolean,
  hasCampusTransfer?: boolean
): UIOnboardingScenario {
  // Annual update has highest priority if overdue
  if (status?.annual_update_overdue) {
    return 'annual-profile-update';
  }

  // Campus transfer
  if (hasCampusTransfer) {
    return 'campus-transfer';
  }

  // Role change (promotion/transfer)
  if (hasRoleChange) {
    return 'role-change';
  }

  // First-time user
  if (isNewUser || !status) {
    return 'first-time-user';
  }

  // Default to first-time user if status is incomplete
  return 'first-time-user';
}
